// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { getMonetaryValue } from '@services/formatService';
import { describePerUseLine, type PerUseLine } from '@hopdrive/sdk-ar';

import { css } from '@emotion/css';
import {
  Paper,
  Theme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

// TYPES ---------------------------------------------------------------- //

type InvoiceDetailsProductsChargesProps = {
  products?: any[]; // Array of archarges with type='product'
  /** The invoice's per-use fees as one condensed line, from sdk-ar. */
  perUseLine?: PerUseLine | null;
  /** Charges no move or product holds, such as a manual charge, listed like products. From sdk-ar. */
  otherCharges?: any[];
};

// COMPONENT ---------------------------------------------------------------- //

const InvoiceDetailsProductsCharges = ({ products, perUseLine, otherCharges }: InvoiceDetailsProductsChargesProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const headers = [
    { id: `PRODUCT`, align: `left` as const, label: `Product` },
    { id: `DESCRIPTION`, align: `left` as const, label: `Description` },
    { id: `COST`, align: `right` as const, label: `Cost` },
  ];

  const lineCharges = [...(products ?? []), ...(otherCharges ?? [])];
  const rows: any[] = lineCharges.map(charge => {
    return {
      PRODUCT: charge.name || null,
      DESCRIPTION: charge.notes || null,
      COST: getMonetaryValue(charge.amount, true) || null,
      charge: charge,
    };
  });

  if (!lineCharges.length && !perUseLine) return null;

  return (
    <div className={cls.products}>
      <Paper variant='custom'>
        <div style={{ padding: `16px` }}>
          <Typography variant='h5' sx={{ lineHeight: 1, fontWeight: 600 }}>
            {otherCharges?.length ? 'Products & Other Charges' : 'Products List'}
          </Typography>
        </div>

        <TableContainer>
          <Table size='small'>
            <TableHead>
              <TableRow>
                {headers?.map(header => {
                  return (
                    <TableCell key={`products-head-cell-${header?.id}`} align={header?.align || `left`}>
                      <Typography sx={clsx.headLabelText}>{header?.label || `Label`}</Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {rows?.map((row, i) => {
                const rowCells = [
                  {
                    value: row.PRODUCT || `-`,
                    align: `left` as const,
                  },
                  {
                    value: row.DESCRIPTION || `-`,
                    align: `left` as const,
                  },
                  {
                    value: row.COST || `-`,
                    align: `right` as const,
                  },
                ];

                return (
                  <React.Fragment key={`product-row-${i}`}>
                    <TableRow>
                      {rowCells && rowCells.length > 0
                        ? rowCells.map((cell, i2) => {
                            return (
                              <TableCell
                                key={`atr-row-${i}-col-${i2}`}
                                align={cell?.align || `left`}
                                style={{
                                  borderBottom: i >= rows.length - 1 && !perUseLine ? `none` : `1px solid ${theme?.palette?.divider}`,
                                }}
                              >
                                {cell?.value || null}
                              </TableCell>
                            );
                          })
                        : null}
                    </TableRow>
                  </React.Fragment>
                );
              })}

              {/* The condensed per-use line */}
              {perUseLine && (
                <TableRow>
                  <TableCell style={{ borderBottom: 'none' }}>
                    <Typography sx={clsx.rowText}>{perUseLine.label}</Typography>
                  </TableCell>
                  <TableCell style={{ borderBottom: 'none' }}>
                    <Typography sx={clsx.rowText}>{describePerUseLine(perUseLine)}</Typography>
                  </TableCell>
                  <TableCell align="right" style={{ borderBottom: 'none' }}>
                    <Typography sx={clsx.rowText}>{getMonetaryValue(perUseLine.amount, true)}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (_theme: Theme) => ({
  products: css`
    position: relative;
  `,
});

const useSxStyles = (_theme: Theme) => ({
  headLabelText: {
    fontSize: '12px',
    fontWeight: 600,
  },
  rowText: {
    fontSize: '14px',
    fontWeight: 400,
  },
});

// EXPORT ---------------------------------------------------------------- //

export default InvoiceDetailsProductsCharges;
